import { fireStore, Collections } from '../../../networking/firebase';
import { docMetaData, getDocData } from '../../../networking/utils';
import { createNotification } from '../../notifications/reducer';
import { t } from '../../../i18n';
import { isTimestamp } from '../../../utils/formatTime';
import { isDate } from 'moment';
import { selectCurrentWorkspaceId } from 'core/modules/app/selectors';

export const fetchRecurrings = (allItems, workspace) => {
    const query = fireStore
        .collection(Collections.recurring)
        .where('workspace_agency', '==', workspace)
        .orderBy('name_lower', 'asc');

    if (allItems) {
        return query
            .get()
            .then(response => response.docs.map(doc => getDocData(doc)));
    } else {
        return query
            .where('meta.doc_status', '==', DocStatus.Active)
            .get()
            .then(response => response.docs.map(doc => getDocData(doc)));
    }
};

export const getRecurrings = (allItems) => (dispatch, getState) => {
    return fetchRecurrings(allItems, selectCurrentWorkspaceId(getState()));
};

export const fetchRecurringById = (id, workspace) => {
    return fireStore
        .collection(Collections.recurring)
        .doc(id)
        .get()
        .then(doc => {
            if (doc.exists) {
                const data = getDocData(doc);

                if (data.workspace_agency === workspace) {
                    data.recurring.start_date_orig = data.recurring.start_date;
                    return data;
                }
            }
            return Promise.reject(new Error(t('errors.not_found')));
        });
};

export const getRecurringById = id => async (dispatch, getState) => {
    return fetchRecurringById(id, selectCurrentWorkspaceId(getState()));
};

export const storeRecurring = ({ id, ...data }, initialDataToCompare) => async (
    dispatch,
    getState
) => {
    const query = fireStore.collection(Collections.recurring);

    // add workspace
    data.workspace_agency = selectCurrentWorkspaceId(getState());
    data.meta = docMetaData(getState());
    // add lowercase name field to order by
    data.name_lower = data.name.toLowerCase();

    // When a new start date was chosen, change the next trigger value.
    if (
        isTimestamp(initialDataToCompare.recurring.start_date_orig) &&
        initialDataToCompare.recurring.start_date_orig.toDate().getTime() !==
        data.recurring.start_date.getTime()
    ) {
        data.recurring.next_trigger = data.recurring.start_date;
    }

    // When it is a new recurring sales and a start date was set.
    if (
        initialDataToCompare.recurring.start_date === null &&
        isDate(data.recurring.start_date)
    ) {
        data.recurring.next_trigger = data.recurring.start_date;
    }

    delete data.recurring.start_date_orig;

    let isUpdate = true;
    if (id) {
        await query.doc(id).set(data);
    } else {
        const ref = await query.add(data);
        id = ref.id;
        isUpdate = false;
    }

    if (isUpdate) {
        dispatch(
            createNotification({
                type: 'success',
                message: t('recurring.notifications.update'),
            })
        );
    } else {
        dispatch(
            createNotification({
                type: 'success',
                message: t('recurring.notifications.create'),
            })
        );
    }

    return { ...data, id };
};

export const deleteRecurring = data => async dispatch => {
    await fireStore
        .collection(Collections.recurring)
        .doc(data.id)
        .delete();

    return dispatch(
        createNotification({
            type: 'error',
            message: t('recurring.notifications.delete'),
        })
    );
};
